/* Curated icon registry over lucide-react. Maps the prototype's kebab-case
   icon names (also used in seed data) to statically-imported glyphs — so only
   the icons we actually use are bundled (no per-icon dynamic chunks). */
import {
  Activity,
  AlarmClock,
  ArrowDownLeft,
  BarChart3,
  Building2,
  Cloud,
  Columns3,
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  ArrowUpRight,
  Ban,
  BookMarked,
  Bell,
  Building,
  Calendar,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Circle,
  CircleAlert,
  CircleHelp,
  Compass,
  LifeBuoy,
  ClipboardCheck,
  Clock,
  Download,
  Eraser,
  ExternalLink,
  Eye,
  EyeOff,
  Users,
  UserPlus,
  UserCog,
  FileSearch,
  Group,
  SlidersHorizontal,
  Factory,
  FileDown,
  FilePlus,
  FileText,
  FileWarning,
  FileX,
  Files,
  Flag,
  Flame,
  Folder,
  FolderOpen,
  FolderTree,
  GitCommitVertical,
  FolderSync,
  Gauge,
  Grid2x2,
  Hammer,
  HardHat,
  Hash,
  Highlighter,
  History,
  Inbox,
  Info,
  Layers,
  LayoutDashboard,
  LayoutList,
  LayoutPanelLeft,
  ListChecks,
  ListFilter,
  ListOrdered,
  Loader,
  Lock,
  LogOut,
  type LucideIcon,
  MapPin,
  MessageSquare,
  MessageSquarePlus,
  MessageSquareReply,
  MessageSquareWarning,
  Moon,
  MousePointer2,
  MoveUpRight,
  Package,
  PackageCheck,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Paperclip,
  Pause,
  Pencil,
  Play,
  Plus,
  Printer,
  Repeat,
  Replace,
  RotateCcw,
  Save,
  Rows2,
  Rows3,
  ScanSearch,
  Search,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  Siren,
  Square,
  SquareDashed,
  Stamp,
  Sun,
  Table2,
  Timer,
  Trash2,
  TriangleAlert,
  Type,
  Undo2,
  Upload,
  User,
  UserRound,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'

/* Имени, которого здесь нет, Icon подставляет CircleAlert. Одиннадцать имён,
   выбранных на экранах, в реестр так и не попали — и все одиннадцать мест
   рисовали значок тревоги: загрузка выглядела как отказ. Молчаливая подстановка
   тем и опасна, что ничего не ломает. */
const REGISTRY: Record<string, LucideIcon> = {
  activity: Activity,
  'alarm-clock': AlarmClock,
  'alert-triangle': TriangleAlert,
  'bar-chart-3': BarChart3,
  'building-2': Building2,
  cloud: Cloud,
  'columns-3': Columns3,
  'arrow-down-left': ArrowDownLeft,
  'arrow-left': ArrowLeft,
  'arrows-right-left': ArrowRightLeft,
  'arrow-right': ArrowRight,
  'arrow-up-right': ArrowUpRight,
  bell: Bell,
  building: Building,
  calendar: Calendar,
  'calendar-days': CalendarDays,
  check: Check,
  'check-check': CheckCheck,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'chevrons-up-down': ChevronsUpDown,
  circle: Circle,
  'circle-alert': CircleAlert,
  'circle-help': CircleHelp,
  compass: Compass,
  'life-buoy': LifeBuoy,
  'clipboard-check': ClipboardCheck,
  clock: Clock,
  download: Download,
  eraser: Eraser,
  'external-link': ExternalLink,
  eye: Eye,
  users: Users,
  'user-plus': UserPlus,
  'user-cog': UserCog,
  ban: Ban,
  'book-marked': BookMarked,
  'file-search': FileSearch,
  group: Group,
  'sliders-horizontal': SlidersHorizontal,
  'eye-off': EyeOff,
  factory: Factory,
  'file-down': FileDown,
  'file-plus': FilePlus,
  'file-text': FileText,
  'file-warning': FileWarning,
  'file-x': FileX,
  files: Files,
  flag: Flag,
  flame: Flame,
  folder: Folder,
  'folder-open': FolderOpen,
  'folder-tree': FolderTree,
  'git-commit-vertical': GitCommitVertical,
  'folder-sync': FolderSync,
  gauge: Gauge,
  'grid-2x2': Grid2x2,
  hammer: Hammer,
  hash: Hash,
  'hard-hat': HardHat,
  highlighter: Highlighter,
  history: History,
  inbox: Inbox,
  info: Info,
  layers: Layers,
  'layout-dashboard': LayoutDashboard,
  'layout-list': LayoutList,
  'layout-panel-left': LayoutPanelLeft,
  'list-checks': ListChecks,
  'list-filter': ListFilter,
  'list-ordered': ListOrdered,
  loader: Loader,
  lock: Lock,
  'log-out': LogOut,
  'map-pin': MapPin,
  'message-square': MessageSquare,
  'message-square-plus': MessageSquarePlus,
  'message-square-reply': MessageSquareReply,
  'message-square-warning': MessageSquareWarning,
  moon: Moon,
  'mouse-pointer-2': MousePointer2,
  'move-up-right': MoveUpRight,
  package: Package,
  'package-check': PackageCheck,
  'panel-left-close': PanelLeftClose,
  'panel-left-open': PanelLeftOpen,
  'panel-right-close': PanelRightClose,
  'panel-right-open': PanelRightOpen,
  paperclip: Paperclip,
  pause: Pause,
  play: Play,
  pencil: Pencil,
  plus: Plus,
  printer: Printer,
  repeat: Repeat,
  /* «Сменить проект» в меню пользователя: имя выбрано на экране, а в реестр
     не попало — и вместо замены рисовался значок тревоги. */
  replace: Replace,
  'rotate-ccw': RotateCcw,
  save: Save,
  'rows-2': Rows2,
  'rows-3': Rows3,
  'scan-search': ScanSearch,
  search: Search,
  send: Send,
  settings: Settings,
  'shield-check': ShieldCheck,
  shield: Shield,
  siren: Siren,
  square: Square,
  'square-dashed': SquareDashed,
  stamp: Stamp,
  sun: Sun,
  'table-2': Table2,
  timer: Timer,
  'trash-2': Trash2,
  type: Type,
  'undo-2': Undo2,
  upload: Upload,
  user: User,
  'user-round': UserRound,
  x: X,
  'zoom-in': ZoomIn,
  'zoom-out': ZoomOut,
}

export interface IconProps {
  name: string
  size?: number
  className?: string
  strokeWidth?: number
  color?: string
  style?: import('react').CSSProperties
  title?: string
}

/* Имена, о которых уже пожаловались. Ругаемся по одному разу: иконка рисуется
   в каждой строке реестра, и без этого консоль забьётся одной и той же строкой. */
const complained = new Set<string>()

export function Icon({ name, size = 16, className, strokeWidth = 1.75, color, style, title }: IconProps) {
  const Glyph = REGISTRY[name]
  /* Подстановка молчала — и потому дважды доезжала до стенда: сначала одиннадцать
     имён рисовались значком тревоги (загрузка выглядела как отказ), потом ещё
     пятнадцать, включая всю панель рисования в просмотрщике. Пропущенное имя
     ничего не ломает, поэтому заметить его можно только так. */
  if (!Glyph && import.meta.env.DEV && !complained.has(name)) {
    complained.add(name)
    console.warn(`Icon: имени «${name}» нет в реестре ds/icons.tsx — рисуется значок тревоги`)
  }
  const Fallback = Glyph ?? CircleAlert
  return <Fallback size={size} strokeWidth={strokeWidth} className={className} color={color} style={style} aria-label={title} aria-hidden={title ? undefined : true} />
}
